/**
 * Tibbs's playback (docs/TIBBS-VOICE.md 5.6): every clip decoded first, then
 * scheduled gaplessly on the game's one AudioContext clock (name, 150 ms, line;
 * 250 ms between lead and tail). Captions and the ceremony's visual beats fire
 * when the clock reaches them, so a call or Siri that freezes the clock pauses
 * everything together and it carries on where it stopped.
 *
 * iOS: the page's audio session is "ambient" (effects mix with the player's
 * music and obey the silent switch). While Tibbs talks it is held at
 * "playback", so he is heard with the silent switch on, the same as Single
 * Didge's Readout, then handed back. Effects duck 6 dB under him.
 *
 * Muted (the top-bar sound switch) or no audio at all: the same playlist runs
 * on a wall clock with captions only, slow enough to read.
 */
import { holdPlaybackSession } from '../lib/audioKeeper';
import { audioContext, duckEffects, unlockAudio } from '../lib/sound';
import { nameBytes } from './names';
import { readingTime } from './plan';
import type { Cue, Step } from './plan';

export type PlayState = 'loading' | 'playing' | 'blocked' | 'done' | 'stopped';

export interface MusicBed {
  url: string;
  /** Seconds of music alone before the first word. */
  soloS: number;
}

export interface PlayOpts {
  steps: Step[];
  /** Linear voice gain (settings and mute); 0 plays captions only. */
  gain: number;
  /** Start this many ms from now (an online table starts together). */
  delayMs?: number;
  music?: MusicBed | null;
  onCue: (cue: Cue) => void;
  onState: (s: PlayState) => void;
}

export interface Playback {
  stop(): void;
  /** A tap after 'blocked' (audio was locked): start now. */
  resume(): void;
  setGain(g: number): void;
}

/** Ceremony mix, after Single Didge's ceremony-mix.js: music ducks to −16 dB under the voice over 0.6 s and fades out on a half-sine 2.5 s after the last word. */
export const DUCK = Math.pow(10, -16 / 20);
export const DUCK_S = 0.6;
export const TAIL_S = 2.5;
const FADE_S = 0.12;
const START_LEAD_S = 0.12;
/** Worker-rendered names are not loudness-normalized like the pack: bring them to the pack's speech level here. */
const NAME_RMS = 0.11;

// ---------------------------------------------------------------------------
// Decoding (cached per context)
// ---------------------------------------------------------------------------

const decoded = new WeakMap<AudioContext, Map<string, Promise<AudioBuffer | null>>>();

function decode(c: AudioContext, bytes: ArrayBuffer): Promise<AudioBuffer> {
  return new Promise((res, rej) => {
    try {
      const p = c.decodeAudioData(bytes, res, rej);
      p?.then?.(res, rej);
    } catch (e) {
      rej(e);
    }
  });
}

async function bytesFor(url: string): Promise<ArrayBuffer | null> {
  if (url.startsWith('/api/voice/name/')) return nameBytes(url);
  try {
    const r = await fetch(url);
    return r.ok ? await r.arrayBuffer() : null;
  } catch {
    return null;
  }
}

export function bufferFor(c: AudioContext, url: string): Promise<AudioBuffer | null> {
  let m = decoded.get(c);
  if (!m) decoded.set(c, (m = new Map()));
  const hit = m.get(url);
  if (hit) return hit;
  const p = (async () => {
    const bytes = await bytesFor(url);
    if (!bytes) return null;
    const buf = await decode(c, bytes);
    return url.startsWith('/api/voice/name/') ? normalizeName(c, buf) : buf;
  })().catch(() => null);
  m.set(url, p);
  p.then((b) => {
    if (!b) m!.delete(url); // a miss can be retried later (back online)
  });
  return p;
}

/** Trim the silence around a name and match its loudness to the pack (pure math on the samples). */
export function trimAndLevel(data: Float32Array, rate: number, targetRms = NAME_RMS): { start: number; end: number; gain: number } {
  const thr = 0.015;
  let start = 0;
  let end = data.length;
  while (start < end && Math.abs(data[start]!) < thr) start++;
  while (end > start && Math.abs(data[end - 1]!) < thr) end--;
  const pad = Math.floor(rate * 0.03);
  start = Math.max(0, start - pad);
  end = Math.min(data.length, end + pad);
  let sum = 0;
  let n = 0;
  let peak = 0;
  for (let i = start; i < end; i++) {
    const v = Math.abs(data[i]!);
    if (v > peak) peak = v;
    if (v > thr) {
      sum += v * v;
      n++;
    }
  }
  const rms = n ? Math.sqrt(sum / n) : 0;
  let gain = rms > 0 ? targetRms / rms : 1;
  if (peak * gain > 0.95) gain = 0.95 / peak;
  return { start, end: Math.max(end, start + 1), gain };
}

function normalizeName(c: AudioContext, buf: AudioBuffer): AudioBuffer {
  try {
    const data = buf.getChannelData(0);
    const { start, end, gain } = trimAndLevel(data, buf.sampleRate);
    const out = c.createBuffer(1, end - start, buf.sampleRate);
    const o = out.getChannelData(0);
    for (let i = start; i < end; i++) o[i - start] = data[i]! * gain;
    return out;
  } catch {
    return buf;
  }
}

async function running(c: AudioContext, ms = 600): Promise<boolean> {
  if (c.state === 'running') return true;
  try {
    await Promise.race([Promise.resolve(c.resume()), new Promise((r) => setTimeout(r, ms))]);
  } catch {
    /* ignore */
  }
  return (c.state as string) === 'running';
}

// ---------------------------------------------------------------------------
// Playback
// ---------------------------------------------------------------------------

let current: { stop: () => void } | null = null;

/** Stop whatever Tibbs is saying (Next hole, leaving the table). */
export function stopTibbs(): void {
  current?.stop();
}

export function play(opts: PlayOpts): Playback {
  current?.stop();
  let stopped = false;
  let finished = false;
  let blockedStart: (() => void) | null = null;
  const timers: ReturnType<typeof setTimeout>[] = [];
  let poll: ReturnType<typeof setInterval> | null = null;
  let release: () => void = () => {};
  let teardownAudio: (() => void) | null = null;
  let voiceGain: GainNode | null = null;
  let gain = opts.gain;
  const c = gain > 0 ? audioContext() : null;

  const end = (state: 'done' | 'stopped') => {
    if (finished) return;
    finished = true;
    timers.forEach(clearTimeout);
    if (poll) clearInterval(poll);
    teardownAudio?.();
    release();
    duckEffects(false);
    if (current === handle) current = null;
    opts.onState(state);
  };

  const handle = {
    stop() {
      if (stopped || finished) return;
      stopped = true;
      end('stopped');
    },
    resume() {
      unlockAudio();
      const go = blockedStart;
      blockedStart = null;
      go?.();
    },
    setGain(g: number) {
      gain = g;
      if (voiceGain && c) {
        voiceGain.gain.cancelScheduledValues(c.currentTime);
        voiceGain.gain.setTargetAtTime(g, c.currentTime, 0.05);
      }
    },
  };
  current = handle;
  opts.onState('loading');

  if (!c) {
    // Captions only: a wall clock, each line up long enough to read.
    const start = () => {
      opts.onState('playing');
      let t = 0;
      let lineStart = 0;
      let lastCaption = '';
      const flushLine = () => {
        // Hold the previous line long enough to read before the next one.
        const need = readingTime(lastCaption);
        if (t - lineStart < need) t = lineStart + need;
      };
      for (const s of opts.steps) {
        if (s.kind === 'cue') {
          if (s.cue.reveal === undefined) {
            if (lastCaption) flushLine();
            lineStart = t;
            lastCaption = s.cue.caption;
          }
          const at = t;
          timers.push(setTimeout(() => !finished && opts.onCue(s.cue), at * 1000));
        } else if (s.kind === 'clip') t += s.dur;
        else if (s.kind === 'name') t += 0.6;
        else t += s.ms / 1000;
      }
      if (lastCaption) flushLine();
      timers.push(setTimeout(() => end('done'), t * 1000 + 300));
    };
    timers.push(setTimeout(start, Math.max(0, opts.delayMs ?? 0)));
    return handle;
  }

  const startAt = Date.now() + Math.max(0, opts.delayMs ?? 0);
  void (async () => {
    const urls = [...new Set(opts.steps.flatMap((s) => (s.kind === 'clip' || s.kind === 'name' ? [s.url] : [])))];
    const bufs = new Map<string, AudioBuffer | null>();
    const musicP = opts.music ? bufferFor(c, opts.music.url) : Promise.resolve(null);
    await Promise.all(urls.map(async (u) => bufs.set(u, await bufferFor(c, u))));
    const music = await Promise.race([musicP, new Promise<null>((r) => setTimeout(() => r(null), 8000))]);
    if (stopped) return;
    const wait = startAt - Date.now();
    if (wait > 0) await new Promise((r) => setTimeout(r, wait));
    if (stopped) return;
    const begin = () => {
      if (stopped) return;
      release = holdPlaybackSession();
      duckEffects(true);
      const out = c.createGain();
      out.gain.value = 1;
      out.connect(c.destination);
      voiceGain = c.createGain();
      voiceGain.gain.value = gain;
      voiceGain.connect(out);
      const srcs: AudioBufferSourceNode[] = [];
      const t0 = c.currentTime + START_LEAD_S;
      const solo = music && opts.music ? opts.music.soloS : 0;
      let t = t0 + solo;
      const cues: [number, Cue][] = [];
      for (const s of opts.steps) {
        if (s.kind === 'cue') cues.push([t, s.cue]);
        else if (s.kind === 'gap') t += s.ms / 1000;
        else {
          const b = bufs.get(s.url);
          if (!b) continue;
          const src = c.createBufferSource();
          src.buffer = b;
          src.connect(voiceGain);
          src.start(t);
          srcs.push(src);
          t += b.duration;
        }
      }
      const voiceEnd = t;
      let stopAt = voiceEnd + 0.1;
      let musicEnv: GainNode | null = null;
      if (music) {
        musicEnv = c.createGain();
        musicEnv.connect(voiceGain);
        const g = musicEnv.gain;
        g.setValueAtTime(1, t0);
        g.setValueAtTime(1, t0 + solo);
        g.linearRampToValueAtTime(DUCK, t0 + solo + DUCK_S);
        g.setValueAtTime(DUCK, voiceEnd);
        const curve = new Float32Array(50).map((_, i) => DUCK * Math.cos((Math.PI / 2) * (i / 49)));
        try {
          g.setValueCurveAtTime(curve, voiceEnd, TAIL_S);
        } catch {
          g.linearRampToValueAtTime(0, voiceEnd + TAIL_S);
        }
        const m = c.createBufferSource();
        m.buffer = music;
        m.connect(musicEnv);
        m.start(t0);
        m.stop(Math.min(t0 + music.duration, voiceEnd + TAIL_S + 0.05));
        srcs.push(m);
        stopAt = Math.max(stopAt, Math.min(t0 + music.duration, voiceEnd + TAIL_S));
      }
      teardownAudio = () => {
        const now = c.currentTime;
        try {
          out.gain.cancelScheduledValues(now);
          out.gain.setValueAtTime(out.gain.value, now);
          out.gain.linearRampToValueAtTime(0, now + FADE_S);
        } catch {
          /* ignore */
        }
        setTimeout(() => {
          for (const s of srcs) {
            try {
              s.stop();
            } catch {
              /* already stopped */
            }
          }
          try {
            out.disconnect();
          } catch {
            /* ignore */
          }
        }, FADE_S * 1000 + 30);
      };
      opts.onState('playing');
      let next = 0;
      poll = setInterval(() => {
        const now = c.currentTime;
        while (next < cues.length && cues[next]![0] <= now + 0.02) opts.onCue(cues[next++]![1]);
        if (now >= stopAt) end('done');
      }, 40);
      try {
        (window as unknown as { __tibbsLog?: unknown[] }).__tibbsLog?.push({ at: Date.now(), clips: srcs.length, voiceS: Number((voiceEnd - t0 - solo).toFixed(2)), music: !!music });
      } catch {
        /* ignore */
      }
    };
    if (await running(c)) begin();
    else {
      opts.onState('blocked');
      blockedStart = () => {
        void running(c, 1500).then((ok) => (ok ? begin() : end('stopped')));
      };
    }
  })();
  return handle;
}
